/**
 * InstallmentsTimeline — Cronograma de parcelas clicáveis.
 *
 * Barra horizontal colorida por status (paga = emerald, atual = brand,
 * vencida = red, futuras = slate). Clicar em uma parcela em aberto
 * navega para o link público daquela parcela. Quando o backend ainda
 * não tem slug ativo para a parcela, o clique dispara on-demand a
 * geração via edge action `ensure_installment_session` e só então
 * navega.
 *
 * RELEASE: 2026-06-21 v1.0.0 — Versão inicial.
 * RELEASE: 2026-06-21 v1.1.0 — Geração on-demand de slug para parcelas
 *                              sem sessão ativa (requer onEnsure injetado
 *                              pelo container).
 * RELEASE: 2026-09-18 v1.1.1 — Correção de fuso: datas puras (YYYY-MM-DD)
 *                              deixam de passar por `new Date`, que exibia o
 *                              dia anterior em America/Sao_Paulo (UTC-3).
 */


import { useMemo } from 'react';
import { Loader2 } from 'lucide-react';

export interface InstallmentEntry {
  /** UUID real da parcela — necessário para gerar slug on-demand. */
  id?: string;
  numero_parcela: number;
  total_parcelas: number;
  valor: number;
  data_vencimento: string | null;
  status: 'aberta' | 'pago' | 'vencida' | 'cancelada' | 'estornado' | string;
  slug: string | null;
  is_current: boolean;
}

interface Props {
  installments: InstallmentEntry[];
  /** Slug atualmente aberto — usado para marcar "atual" e como pai para ensure. */
  currentSlug?: string;
  /** Navega para um slug já resolvido. */
  onNavigate: (slug: string) => void;
  /**
   * Gera/reaproveita slug para uma parcela sem sessão ativa.
   * Opcional — quando ausente, parcelas sem slug ficam inertes.
   */
  onEnsure?: (parcelaId: string) => Promise<string | null>;
  /** UUID da parcela em loading (ensure pendente). */
  pendingParcelaId?: string | null;
}

function formatBRL(v: number): string {
  return v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

/**
 * Formata a data no padrão curto "18 set".
 * v1.1.1: DATE puro (YYYY-MM-DD) é montado como data LOCAL (new Date(ano, mês-1,
 * dia)) para não sofrer o deslocamento UTC→local que exibia o dia anterior.
 */
function shortDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    const puro = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    const d = puro
      ? new Date(Number(puro[1]), Number(puro[2]) - 1, Number(puro[3]))
      : new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
  } catch {
    return iso;
  }
}


function statusMeta(status: string, isCurrent: boolean) {
  if (status === 'pago') {
    return { bar: 'bg-emerald-500', label: 'Paga', labelCls: 'text-emerald-600' };
  }
  if (status === 'vencida') {
    return { bar: 'bg-red-500', label: 'Vencida', labelCls: 'text-red-600' };
  }
  if (status === 'cancelada' || status === 'estornado') {
    return { bar: 'bg-slate-300', label: 'Cancelada', labelCls: 'text-slate-400' };
  }
  if (isCurrent) {
    return {
      bar: 'bg-[var(--brand-primary)]',
      label: 'Pagar agora',
      labelCls: 'text-emerald-600',
    };
  }
  return { bar: 'bg-slate-200', label: shortDate(null), labelCls: 'text-slate-400' };
}

export function InstallmentsTimeline({
  installments,
  currentSlug,
  onNavigate,
  onEnsure,
  pendingParcelaId,
}: Props) {
  const items = useMemo(() => {
    return installments
      .slice()
      .sort((a, b) => a.numero_parcela - b.numero_parcela)
      .map((p) => {
        const isCurrent = p.is_current || (currentSlug ? p.slug === currentSlug : false);
        const meta = statusMeta(p.status, isCurrent);
        const sub =
          p.status === 'pago' || p.status === 'cancelada' || p.status === 'estornado' || p.status === 'vencida'
            ? meta.label
            : isCurrent
              ? 'Pagar agora'
              : shortDate(p.data_vencimento);
        const isTerminal = p.status === 'pago' || p.status === 'cancelada' || p.status === 'estornado';
        // Clicável quando: não é a atual, não é terminal e (tem slug OU dá pra gerar on-demand)
        const clickable =
          !isCurrent && !isTerminal && (Boolean(p.slug) || (Boolean(p.id) && Boolean(onEnsure)));
        return { p, meta, sub, isCurrent, clickable };
      });
  }, [installments, currentSlug, onEnsure]);

  if (items.length <= 1) return null;

  const handleClick = async (p: InstallmentEntry) => {
    if (p.slug) {
      onNavigate(p.slug);
      return;
    }
    if (!p.id || !onEnsure) return;
    const newSlug = await onEnsure(p.id);
    if (newSlug) onNavigate(newSlug);
  };

  return (
    <section className="px-3 md:px-9 py-5 md:py-10">
      {/* Trilho interno alinhado ao card de resumo (~15% menor que a área útil externa). */}
      <div className="md:max-w-[56.5rem] md:mx-auto">
        <h3 className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-3 md:mb-5">
          Cronograma de Parcelas
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 md:gap-4">
        {items.map(({ p, meta, sub, isCurrent, clickable }) => {
          const isPending = Boolean(p.id) && pendingParcelaId === p.id;
          const inner = (
            <>
              <div className={`h-2 rounded-full ${meta.bar} mb-3 transition-all`} />
              <p
                className={`text-xs font-bold ${
                  isCurrent ? 'text-slate-900' : p.status === 'pago' ? 'text-slate-700' : 'text-slate-400'
                }`}
              >
                Parcela {p.numero_parcela}/{p.total_parcelas}
              </p>
              <p className={`text-[10px] font-medium ${meta.labelCls} flex items-center gap-1`}>
                {isPending && <Loader2 className="w-3 h-3 animate-spin" />}
                {isPending ? 'Abrindo…' : sub}
              </p>
              <p className="text-[10px] text-slate-400 mt-0.5">{formatBRL(p.valor)}</p>
            </>
          );

          if (clickable) {
            return (
              <button
                key={p.numero_parcela}
                type="button"
                disabled={isPending}
                onClick={() => handleClick(p)}
                className="text-left group hover:-translate-y-0.5 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand-primary)] rounded-lg p-1 -m-1 disabled:opacity-60 disabled:pointer-events-none"
              >
                {inner}
              </button>
            );
          }
          return (
            <div
              key={p.numero_parcela}
              className={`p-1 -m-1 ${isCurrent ? '' : 'opacity-90'}`}
              aria-disabled={!clickable}
            >
              {inner}
            </div>
          );
        })}
        </div>
      </div>
    </section>
  );
}
